/**
 * ALOHA Cart Drawer - Hi-Fi Design
 * Vanilla JS cart controller aligned with wireframe-handoff specs
 *
 * Features:
 * - Dual-threshold progress bar ($40 ship / $75 gift)
 * - Circles-style autoship tier indicator
 * - Segmented One-time / Autoship toggle per line item
 * - SavedBy shipping protection integration
 * - Upsell rail with product tiles
 * - Full accessibility (focus trap, ESC close, ARIA)
 */

(function() {
  'use strict';

  // Money formatter
  function formatMoney(cents, showDecimals = true) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = parseInt(cents, 10) || 0;
    const value = (cents / 100).toFixed(showDecimals ? 2 : 0);
    return '$' + value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  class AlohaCart {
    constructor() {
      this.cart = document.getElementById('aloha-cart');
      if (!this.cart) return;

      // DOM refs
      this.drawer = document.getElementById('aloha-cart-drawer');
      this.overlay = document.getElementById('aloha-cart-overlay');
      this.closeBtn = document.getElementById('aloha-cart-close');
      this.continueBtn = document.getElementById('ah-continue');
      this.itemsContainer = document.getElementById('ah-items');
      this.emptyState = document.getElementById('ah-empty');
      this.bestsellersContainer = document.getElementById('ah-bestsellers');
      this.footer = document.getElementById('ah-foot');
      this.itemTemplate = document.getElementById('ah-item-template');
      this.tileTemplate = document.getElementById('ah-tile-template');

      // Banner elements - Default
      this.bannerDefault = document.getElementById('ah-banner-default');
      this.bannerFill = document.getElementById('ah-banner-fill');
      this.bannerCopy = document.getElementById('ah-banner-copy');
      this.bannerAmount = document.getElementById('ah-banner-amount');
      this.remaining = document.getElementById('ah-remaining');
      this.pinShip = document.getElementById('ah-pin-ship');
      this.pinGift = document.getElementById('ah-pin-gift');

      // Banner elements - Stretcher
      this.bannerStretcher = document.getElementById('ah-banner-stretcher');
      this.stretcherFill = document.getElementById('ah-stretcher-fill');
      this.stretcherCopy = document.getElementById('ah-stretcher-copy');
      this.stretcherAmount = document.getElementById('ah-stretcher-amount');

      // Banner elements - Sitewide & Launch
      this.bannerSitewide = document.getElementById('ah-banner-sitewide');
      this.bannerLaunch = document.getElementById('ah-banner-launch');

      // Tier elements
      this.tierSection = document.getElementById('ah-tier');
      this.tierSavings = document.getElementById('ah-tier-savings');
      this.tierFill = document.getElementById('ah-tier-fill');
      this.tierMsg = document.getElementById('ah-tier-msg');
      this.tierSteps = [
        document.getElementById('ah-tier-step-1'),
        document.getElementById('ah-tier-step-2'),
        document.getElementById('ah-tier-step-3')
      ];

      // Footer elements
      this.savingsRow = document.getElementById('ah-savings-row');
      this.savingsAmount = document.getElementById('ah-savings-amount');
      this.shipStatus = document.getElementById('ah-ship-status');
      this.shipCheck = document.getElementById('ah-ship-check');
      this.shipText = document.getElementById('ah-ship-text');
      this.totalEl = document.getElementById('ah-total');
      this.checkoutBtn = document.getElementById('ah-checkout');
      this.checkoutAmt = document.getElementById('ah-checkout-amt');
      this.itemCount = document.getElementById('ah-item-count');

      // Upsell
      this.upsellSection = document.getElementById('ah-upsell');
      this.upsellRail = document.getElementById('ah-upsell-rail');
      this.upsellPrev = document.getElementById('ah-upsell-prev');
      this.upsellNext = document.getElementById('ah-upsell-next');

      // Config from data attributes
      const enableUpsellRaw = this.cart.dataset.enableUpsell;
      const upsellCollectionRaw = this.cart.dataset.upsellCollection;

      this.config = {
        freeShippingThreshold: parseInt(this.cart.dataset.freeShipping, 10) || 4000,
        hideShippingBanner: this.cart.dataset.hideShippingBanner === 'true',
        enableGift: this.cart.dataset.enableGift === 'true',
        giftThreshold: parseInt(this.cart.dataset.giftThreshold, 10) || 0,
        promoMode: this.cart.dataset.promoMode || 'default',
        isVip: this.cart.dataset.isVip === 'true',
        vipTier: this.cart.dataset.vipTier || '',
        // Enable upsell if not explicitly set to false, and default collection to best-sellers
        enableUpsell: enableUpsellRaw !== 'false' && enableUpsellRaw !== '',
        upsellCollection: upsellCollectionRaw || 'best-sellers',
        // Cart Stretcher config
        stretcherT1: parseInt(this.cart.dataset.stretcherT1, 10) || 7500,
        stretcherT1Discount: this.cart.dataset.stretcherT1Discount || '$15',
        stretcherT2: parseInt(this.cart.dataset.stretcherT2, 10) || 12500,
        stretcherT2Discount: this.cart.dataset.stretcherT2Discount || '$40',
        // Sitewide
        sitewideMsg: this.cart.dataset.sitewideMsg || '20% off applied to your cart',
        // New Launch
        newLaunchProduct: this.cart.dataset.newLaunchProduct || 'Key Lime',
        newLaunchUrl: this.cart.dataset.newLaunchUrl || '/collections/all',
        // Subscription upgrade CTA text
        subUpgradeText: this.cart.dataset.subUpgradeText || 'Autoship & Save'
      };

      // Mystery Gift (rotating gift-with-purchase). Config is the shop
      // metafield document emitted by the drawer section — the same JSON the
      // pricing function and checkout validation read. The theme only ever
      // adds/swaps/removes the gift line; the $0.00 price comes from the cart
      // transform, and tampered states are blocked at checkout. Thresholds
      // arrive in dollars and are converted to cents once, here.
      // data-mg-eligible is the server-resolved market gate (config
      // allowedCountries vs localization country). Absent attribute (cached
      // markup from an older section render) counts as eligible; mg-active
      // already folds the gate in server-side, this is defense in depth. An
      // ineligible market still runs the reconciler's CLEANUP path — it just
      // never adds a gift.
      this.config.mg = this.parseMysteryGiftConfig(
        this.cart.dataset.mgConfig,
        this.cart.dataset.mgActive === 'true' && this.cart.dataset.mgEligible !== 'false',
        this.cart.dataset.mgMessages
      );
      this.mgBusy = false;
      this.mgPasses = 0;
      this.mgAddBackoffUntil = 0;

      // Upsell products cache
      this.upsellProducts = [];

      // Product cache for selling plans
      this.productCache = {};

      // State
      this.isOpen = false;
      this.isLoading = false;
      this.cartData = null;
      this.lastFocused = null;
      this.scrollY = 0;

      this.init();
    }

    init() {
      this.fetchCart();
      this.bindEvents();
      document.addEventListener('cart:open', () => this.open());
      document.addEventListener('cart:close', () => this.close());
      document.addEventListener('cart:refresh', () => this.fetchCart());
    }

    bindEvents() {
      // Triggers
      document.querySelectorAll('.js-go-cart-trigger, [data-cart-trigger]').forEach(el => {
        el.addEventListener('click', e => { e.preventDefault(); this.open(); });
      });

      // Close
      this.closeBtn?.addEventListener('click', () => this.close());
      this.continueBtn?.addEventListener('click', () => this.close());
      this.overlay?.addEventListener('click', () => this.close());

      // Keyboard
      document.addEventListener('keydown', e => {
        if (!this.isOpen) return;
        if (e.key === 'Escape') { e.preventDefault(); this.close(); }
        if (e.key === 'Tab') this.trapFocus(e);
      });

      // Add to cart forms
      document.querySelectorAll('form[action$="/cart/add"]').forEach(form => {
        form.addEventListener('submit', e => this.handleAddToCart(e, form));
      });


      // Upsell nav
      this.upsellPrev?.addEventListener('click', () => this.scrollUpsell(-1));
      this.upsellNext?.addEventListener('click', () => this.scrollUpsell(1));
    }

    trapFocus(e) {
      const focusable = this.drawer.querySelectorAll(
        'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable.length) return;
      const first = focusable[0], last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault(); first.focus();
      }
    }

    open() {
      if (this.isOpen) return;
      this.isOpen = true;
      this.lastFocused = document.activeElement;
      this.scrollY = window.scrollY;

      document.body.style.top = `-${this.scrollY}px`;
      document.body.classList.add('aloha-cart-open');

      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      this.overlay.classList.add('is-open');

      requestAnimationFrame(() => this.closeBtn?.focus());
      document.dispatchEvent(new CustomEvent('cart:opened'));
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;

      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      this.overlay.classList.remove('is-open');

      document.body.classList.remove('aloha-cart-open');
      document.body.style.top = '';
      window.scrollTo({ top: this.scrollY, behavior: 'instant' });

      this.lastFocused?.focus();
      document.dispatchEvent(new CustomEvent('cart:closed'));
    }

    async fetchCart(cb) {
      try {
        const res = await fetch('/cart.js', { credentials: 'same-origin', headers: { 'Content-Type': 'application/json' } });
        if (!res.ok) throw new Error('Failed to fetch cart');
        this.cartData = await res.json();
        if (await this.dropInvalidBonusLines()) return; // refetched inside
        if (await this.reconcileMysteryGift()) return; // refetched inside
        this.render();
        this.updateHeaderCount();
        document.dispatchEvent(new CustomEvent('CartUpdated', { bubbles: true, detail: this.cartData }));
        if (typeof cb === 'function') cb(this.cartData);
      } catch (err) {
        console.error('ALOHA Cart:', err);
      }
    }

    // Bundle-builder bonus lines (properties._bb_bonus = required item count) are only priced
    // by the BYOB Discount Function while a qualifying build stays at/above that count — either
    // a one-time bundle OR an autoship build (subscription lines), mirroring the Function's
    // qualification. If the customer shrinks below it, drop the bonus line rather than let
    // checkout silently charge full price for it.
    async dropInvalidBonusLines() {
      const items = (this.cartData && this.cartData.items) || [];
      const bonusLines = items.filter((it) => it.properties && it.properties._bb_bonus);
      if (!bonusLines.length) return false;
      const nonBonus = items.filter((it) => !(it.properties && it.properties._bb_bonus));
      const onetimeUnits = nonBonus
        .filter((it) => !it.selling_plan_allocation)
        .reduce((sum, it) => sum + it.quantity, 0);
      const autoshipUnits = nonBonus
        .filter((it) => it.selling_plan_allocation)
        .reduce((sum, it) => sum + it.quantity, 0);
      const qualifying = Math.max(onetimeUnits, autoshipUnits);
      const invalid = bonusLines.filter((it) => qualifying < parseInt(it.properties._bb_bonus, 10));
      if (!invalid.length) return false;
      for (const line of invalid) {
        await fetch('/cart/change.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: line.key, quantity: 0 }),
        });
      }
      this.bonusRemovedNotice = invalid[0].properties._bb_bonus;
      await this.fetchCart();
      return true;
    }

    // Mystery Gift config: dollars -> cents once, GID -> numeric variant id
    // (cart.js JSON exposes numeric variant_id). Any structural problem yields
    // the inert config — the reconciler then only CLEANS UP _mystery_gift-
    // marked lines and never adds one, mirroring the functions' fail-safe.
    parseMysteryGiftConfig(raw, active, rawMessages) {
      const inert = { active: false, tiers: [], message: '', messagesByVariant: {} };
      if (!raw) return inert;
      let cfg;
      try { cfg = JSON.parse(raw); } catch (err) { return inert; }
      if (!cfg || cfg.enabled !== true || !Array.isArray(cfg.tiers) || !cfg.tiers.length) return inert;
      const tiers = [];
      for (const tier of cfg.tiers) {
        const variantId = parseInt(String(tier && tier.variantId || '').split('/').pop(), 10);
        const minCents = Math.round(Number(tier && tier.minSubtotal) * 100);
        if (!variantId || !(minCents > 0)) return inert;
        tiers.push({ variantId, minCents });
      }
      tiers.sort((a, b) => a.minCents - b.minCents);
      // Per-gift messages from the product metafield mystery_gift.cart_message,
      // resolved by the drawer section (keys are variant GIDs).
      const messagesByVariant = {};
      try {
        const map = JSON.parse(rawMessages || '{}');
        for (const gid of Object.keys(map)) {
          const id = parseInt(String(gid).split('/').pop(), 10);
          if (id && typeof map[gid] === 'string' && map[gid]) messagesByVariant[id] = map[gid];
        }
      } catch (err) { /* fall back to the config-level message */ }
      return {
        active,
        tiers,
        message: typeof cfg.message === 'string' ? cfg.message : '',
        messagesByVariant
      };
    }

    // A line is gift-shaped if its variant is a configured tier OR it carries
    // the _mystery_gift marker (catches retired variants after promo rotation
    // — the functions ignore the marker; here it exists precisely so cleanup
    // survives config changes).
    isMysteryGiftItem(item) {
      if (item.properties && item.properties._mystery_gift) return true;
      return this.config.mg.tiers.some((tier) => tier.variantId === item.variant_id);
    }

    // Reconcile the cart to exactly one correct gift line (or none). Runs on
    // every fetchCart, so any mutation path — steppers, quick-adds,
    // direct /cart/add.js tampering — converges on the next cart read.
    // Anti-loop: the gift never counts toward its own basis, so our own
    // mutations can't change the desired outcome (pass 2 lands on the no-op
    // fixed point); mgBusy prevents reentry; mgPasses caps runaway; a failed
    // add (e.g. gift variant OOS -> 422) backs off for 5 minutes instead of
    // hammering. Returns true when it mutated (fetchCart re-entered inside).
    async reconcileMysteryGift() {
      const mg = this.config.mg;
      const items = (this.cartData && this.cartData.items) || [];
      const giftLines = items.filter((it) => this.isMysteryGiftItem(it));
      if (!mg.active && !giftLines.length) return false;
      if (this.mgBusy) return false;
      if ((this.cartData.currency || 'USD') !== 'USD') return false;

      // Same money math as render(): paid line prices minus cart-level code
      // allocations — minus the gift itself, which never funds its own tier.
      const nonGift = items.filter((it) => it.vendor !== 'SavedBy' && !this.isMysteryGiftItem(it));
      const cartLevelDiscount = (this.cartData.cart_level_discount_applications || [])
        .reduce((s, a) => s + (a.total_allocated_amount || 0), 0);
      const basis = Math.max(0, nonGift.reduce((s, i) => s + i.final_line_price, 0) - cartLevelDiscount);

      let desired = null;
      if (mg.active) {
        for (const tier of mg.tiers) {
          if (basis >= tier.minCents) desired = tier; // highest tier reached wins
        }
      }

      // Keep the first exactly-correct line; everything else gift-shaped goes.
      let keeper = null;
      const extras = [];
      for (const line of giftLines) {
        if (!keeper && desired && line.variant_id === desired.variantId &&
            line.quantity === 1 && !line.selling_plan_allocation) {
          keeper = line;
        } else {
          extras.push(line);
        }
      }
      const needsAdd = desired && !keeper && Date.now() > this.mgAddBackoffUntil;
      if (!extras.length && !needsAdd) {
        this.mgPasses = 0;
        return false; // fixed point (or add is backing off)
      }
      if (this.mgPasses >= 2) {
        console.warn('ALOHA Cart: mystery gift reconcile did not converge');
        this.mgPasses = 0;
        return false;
      }

      this.mgBusy = true;
      this.mgPasses += 1;
      try {
        for (const line of extras) {
          await fetch('/cart/change.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: line.key, quantity: 0 }),
          });
        }
        if (needsAdd) {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: desired.variantId,
              quantity: 1,
              // Marker only (rendering + rotation cleanup) — the functions key
              // on variant id and ignore this property entirely. Never send a
              // selling plan here.
              properties: { _mystery_gift: String(desired.variantId) },
            }),
          });
          if (!res.ok) this.mgAddBackoffUntil = Date.now() + 5 * 60 * 1000;
        }
      } finally {
        this.mgBusy = false;
      }
      await this.fetchCart();
      return true;
    }

    async handleAddToCart(e, form) {
      e.preventDefault();
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          body: new FormData(form)
        });
        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.description || 'Failed to add item');
        }
        await this.fetchCart();
        this.open();
      } catch (err) {
        console.error('ALOHA Cart:', err);
        alert(err.message || 'Failed to add item to cart.');
      }
    }

    async updateQty(key, qty) {
      if (this.isLoading) return;
      this.isLoading = true;
      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: qty })
        });
        if (!res.ok) throw new Error('Failed to update');
        await this.fetchCart();
      } catch (err) {
        console.error('ALOHA Cart:', err);
      } finally {
        this.isLoading = false;
      }
    }

    render() {
      if (!this.cartData) return;

      const items = this.cartData.items.filter(i => i.vendor !== 'SavedBy');
      const count = items.reduce((s, i) => s + i.quantity, 0);
      // Order-scope code discounts never appear in final_line_price, only at cart level
      const cartLevelDiscount = (this.cartData.cart_level_discount_applications || [])
        .reduce((s, a) => s + (a.total_allocated_amount || 0), 0);
      const total = Math.max(0, items.reduce((s, i) => s + i.final_line_price, 0) - cartLevelDiscount);

      // Item count
      if (this.itemCount) {
        this.itemCount.textContent = count === 1 ? '1 item' : `${count} items`;
      }

      // Empty vs filled
      if (count === 0) {
        this.showEmpty();
      } else {
        this.showItems(items, total);
      }

      // Transient notice when a bundle-builder bonus item was auto-removed
      if (this.bonusRemovedNotice) {
        const min = this.bonusRemovedNotice;
        this.bonusRemovedNotice = null;
        let n = this.drawer.querySelector('.ah-bonus-notice');
        if (!n) {
          n = document.createElement('div');
          n.className = 'ah-bonus-notice';
          n.style.cssText = 'margin:10px 16px 0;padding:9px 12px;border-radius:8px;background:rgba(242,189,25,.18);color:#2a4636;font-size:12.5px;font-weight:600;';
          const head = this.drawer.querySelector('.ah-head');
          if (head && head.parentNode) head.parentNode.insertBefore(n, head.nextSibling);
        }
        n.textContent = 'Your bonus item was removed — it needs a ' + min + '+ item one-time bundle.';
        clearTimeout(this._bonusNoticeT);
        this._bonusNoticeT = setTimeout(() => { if (n.parentNode) n.parentNode.removeChild(n); }, 8000);
      }
    }

    showEmpty() {
      if (this.itemsContainer) this.itemsContainer.innerHTML = '';
      if (this.emptyState) this.emptyState.style.display = 'flex';
      if (this.footer) this.footer.style.display = 'none';
      if (this.tierSection) this.tierSection.style.display = 'none';
      if (this.upsellSection) this.upsellSection.style.display = 'none';

      // Hide all banners on empty state
      if (this.bannerDefault) this.bannerDefault.style.display = 'none';
      if (this.bannerStretcher) this.bannerStretcher.style.display = 'none';
      if (this.bannerSitewide) this.bannerSitewide.style.display = 'none';
      if (this.bannerLaunch) this.bannerLaunch.style.display = 'none';

      // Load best sellers for empty cart
      this.loadBestsellers();
    }

    async loadBestsellers() {
      if (!this.bestsellersContainer || !this.config.upsellCollection) {
        return;
      }

      // Don't refetch if we already have products
      if (this.upsellProducts.length > 0) {
        this.renderBestsellers();
        return;
      }

      try {
        const handle = this.config.upsellCollection;
        const res = await fetch(`/collections/${handle}/products.json?limit=6`);
        if (!res.ok) throw new Error('Failed to fetch bestsellers');
        const data = await res.json();
        this.upsellProducts = data.products || [];
        this.renderBestsellers();
      } catch (err) {
        console.warn('Bestsellers fetch error:', err);
      }
    }

    renderBestsellers() {
      if (!this.bestsellersContainer || this.upsellProducts.length === 0) return;

      const template = document.getElementById('ah-tile-template');
      if (!template) return;

      this.bestsellersContainer.innerHTML = '';

      this.upsellProducts.slice(0, 3).forEach(product => {
        const variant = product.variants[0];
        if (!variant) return;

        const tile = template.content.cloneNode(true).querySelector('.ah-tile');
        if (!tile) return;

        tile.dataset.productId = product.id;
        tile.dataset.variantId = variant.id;

        const img = tile.querySelector('.ah-tile__media img');
        if (img && product.images && product.images[0]) {
          img.src = this.resizeImg(product.images[0].src, '200x200');
          img.alt = product.title;
        }

        // Set product links (image and title)
        const productUrl = `/products/${product.handle}`;
        const mediaLink = tile.querySelector('.ah-tile__media-link');
        const nameLink = tile.querySelector('.ah-tile__name-link');
        if (mediaLink) mediaLink.href = productUrl;
        if (nameLink) nameLink.href = productUrl;

        this.setTileTitle(tile, product);

        // Price from products.json is a string like "34.99" - convert to cents
        const priceInCents = Math.round(parseFloat(variant.price) * 100);
        const priceEl = tile.querySelector('.ah-tile__price');
        if (priceEl) priceEl.textContent = formatMoney(priceInCents);

        const addBtn = tile.querySelector('.ah-tile__add');
        if (addBtn) {
          addBtn.addEventListener('click', (e) => {
            e.preventDefault();
            this.addToCart(variant.id, 1);
          });
        }

        this.bestsellersContainer.appendChild(tile);
      });
    }

    showItems(items, total) {
      if (this.emptyState) this.emptyState.style.display = 'none';
      if (this.footer) this.footer.style.display = 'block';

      // Fetch and render upsell products
      this.fetchUpsell();

      // Render items
      if (this.itemsContainer) {
        this.itemsContainer.innerHTML = '';
        items.forEach((item, idx) => {
          this.itemsContainer.appendChild(this.renderItem(item, idx));
        });
      }

      // Update banner
      this.updateBanner(total);

      // Update tier
      this.updateTier(items);

      // Update footer
      this.updateFooter(items, total);
    }

    renderItem(item, idx) {
      const tpl = this.itemTemplate.content.cloneNode(true);
      const el = tpl.querySelector('.ah-item');

      el.dataset.key = item.key;
      el.dataset.line = idx + 1;

      // Image
      const img = el.querySelector('.ah-item__media img');
      if (item.image) {
        img.src = this.resizeImg(item.image, '180x');
        img.alt = item.title;
      }

      // Set product links (image and title) — never for the Mystery Gift: its
      // product page is unlisted and the line isn't customer-manageable, so
      // the anchors stay hrefless (renders as plain text/image).
      const mediaLink = el.querySelector('.ah-item__media-link');
      const nameLink = el.querySelector('.ah-item__name-link');
      if (item.url && !this.isMysteryGiftItem(item)) {
        if (mediaLink) mediaLink.href = item.url;
        if (nameLink) nameLink.href = item.url;
      }

      // Title & variant
      el.querySelector('.ah-item__name').textContent = item.product_title;
      el.querySelector('.ah-item__variant').textContent = item.variant_title || '';

      // Mystery Gift line: fully locked — no stepper, no remove, no
      // subscription toggle (the reconciler owns this line's lifecycle, the
      // cart transform owns its price). Renders the config message + FREE.
      const isMysteryGift = this.isMysteryGiftItem(item);
      if (isMysteryGift) {
        el.classList.add('ah-item--gift');
        // Per-gift product metafield first, config-level message as fallback.
        const giftMessage = this.config.mg.messagesByVariant[item.variant_id] || this.config.mg.message;
        if (giftMessage) {
          el.querySelector('.ah-item__variant').textContent = giftMessage;
        }
      }

      // Subscription UI - fetch product selling plans and show toggle
      const subUI = el.querySelector('.ah-sub');
      const hasSubscription = !!item.selling_plan_allocation;

      // Fetch product selling plans asynchronously (never for the gift line)
      if (isMysteryGift) {
        subUI.style.display = 'none';
      } else this.fetchProductSellingPlans(item.handle).then(productData => {
        const canSubscribe = productData.hasSubscription || hasSubscription;

        // Store product data on element for later use
        el.dataset.productHandle = item.handle;

        if (canSubscribe) {
          subUI.style.display = 'block';
          subUI.dataset.plan = hasSubscription ? 'as' : 'ot';

          // Calculate discount % based on total subscription items
          const subscriptionQty = this.cartData.items
            .filter(i => i.selling_plan_allocation && i.vendor !== 'SavedBy')
            .reduce((s, i) => s + i.quantity, 0);

          // For upgrade CTA: show dollar savings (next tier) × quantity
          const potentialQty = subscriptionQty + item.quantity;
          const upgradePct = potentialQty >= 3 ? 25 : potentialQty >= 2 ? 15 : 10;
          const itemPrice = item.original_price || item.price;
          const dollarSavings = Math.round(itemPrice * upgradePct / 100) * item.quantity;

          // Update upgrade CTA with configurable text and dollar savings
          const upgradeCta = el.querySelector('.ah-sub__cta');
          if (upgradeCta) {
            const upgradeText = this.config.subUpgradeText || 'Autoship & Save';
            upgradeCta.innerHTML = `<span class="ah-sub__plus">+</span> ${upgradeText} <span class="ah-sub__dollars">${formatMoney(dollarSavings)}</span>`;
            upgradeCta.addEventListener('click', () => this.switchPlan(item, 'as', el, productData));
          }

          // Bind downgrade link
          const downgradeBtn = el.querySelector('.ah-sub__downgrade');
          if (downgradeBtn) {
            downgradeBtn.addEventListener('click', () => this.switchPlan(item, 'ot', el, productData));
          }

          // Frequency dropdown - populate with actual selling plans
          const freqSelect = el.querySelector('.ah-freq__select');
          if (freqSelect && productData.sellingPlans.length > 0) {
            // Clear and populate with actual selling plans
            freqSelect.innerHTML = '';
            productData.sellingPlans.forEach(sp => {
              const option = document.createElement('option');
              option.value = sp.id;
              option.textContent = sp.name;
              freqSelect.appendChild(option);
            });

            // Set current selection if subscription
            if (hasSubscription && item.selling_plan_allocation) {
              freqSelect.value = item.selling_plan_allocation.selling_plan.id;
            }

            // Handle frequency change
            freqSelect.addEventListener('change', (e) => {
              this.changeFrequency(item, e.target.value, productData);
            });
          }
        } else {
          subUI.style.display = 'none';
        }
      });

      // Quantity (gift line: stepper hidden, quantity is pinned to 1 by the
      // reconciler and enforced server-side by the validation function)
      const qtyEl = el.querySelector('.ah-stepper__qty');
      qtyEl.textContent = item.quantity;

      if (isMysteryGift) {
        el.querySelector('.ah-stepper').style.display = 'none';
      } else {
        el.querySelector('.ah-stepper-minus').addEventListener('click', () => {
          const newQty = item.quantity - 1;
          if (newQty <= 0) this.updateQty(item.key, 0);
          else this.updateQty(item.key, newQty);
        });

        el.querySelector('.ah-stepper-plus').addEventListener('click', () => {
          this.updateQty(item.key, item.quantity + 1);
        });
      }

      // Price
      const priceEl = el.querySelector('.ah-price');
      const strikeEl = el.querySelector('.ah-price-strike');
      const onetimePrice = item.properties?._onetime_variant_price || item.original_price;

      if (isMysteryGift && item.final_line_price === 0) {
        // No strikethrough: the cart transform REPLACES the line price, so the
        // cart JSON's original_price is also 0 — there's no real compare-at
        // value available here. Just FREE.
        priceEl.textContent = 'FREE';
        priceEl.classList.add('discount');
        strikeEl.style.display = 'none';
      } else if (hasSubscription && onetimePrice > item.final_price) {
        priceEl.textContent = formatMoney(item.final_line_price);
        priceEl.classList.add('discount');
        strikeEl.textContent = formatMoney(onetimePrice * item.quantity);
        strikeEl.style.display = 'block';
      } else {
        priceEl.textContent = formatMoney(item.final_line_price);
        strikeEl.style.display = 'none';
      }

      // Remove (hidden for the gift line — the reconciler owns it)
      const removeBtn = el.querySelector('.ah-item__remove');
      if (isMysteryGift) {
        removeBtn.style.display = 'none';
      } else {
        removeBtn.addEventListener('click', () => {
          this.updateQty(item.key, 0);
        });
      }

      return el;
    }

    async switchPlan(item, plan, itemEl, productData = null) {
      const isSubscription = plan === 'as';
      const currentIsSubscription = !!item.selling_plan_allocation;

      if (isSubscription === currentIsSubscription) return;

      try {
        // Get product data if not provided
        if (!productData && item.handle) {
          productData = await this.fetchProductSellingPlans(item.handle);
        }

        // Get selling plan ID from dropdown or first available
        const freqSelect = itemEl?.querySelector('.ah-freq__select');
        let sellingPlanId = freqSelect?.value;

        // If no selection, get first available selling plan
        if (!sellingPlanId && productData?.sellingPlans?.length) {
          sellingPlanId = productData.sellingPlans[0].id;
        }

        // Remove current item
        await fetch('/cart/change.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: item.key, quantity: 0 })
        });

        if (isSubscription && sellingPlanId) {
          // Add with selling_plan (Skio format)
          await fetch('/cart/add.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: [{
                id: item.variant_id,
                quantity: item.quantity,
                selling_plan: sellingPlanId
              }]
            })
          });
        } else {
          // Add one-time
          await fetch('/cart/add.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: [{
                id: item.variant_id,
                quantity: item.quantity
              }]
            })
          });
        }

        await this.fetchCart();
      } catch (err) {
        console.error('ALOHA Cart:', err);
      }
    }

    async changeFrequency(item, newSellingPlanId, productData = null) {
      if (!item.selling_plan_allocation) return;
      if (!newSellingPlanId) return;

      try {
        // Remove current
        await fetch('/cart/change.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: item.key, quantity: 0 })
        });

        // Add with new selling plan
        await fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{
              id: item.variant_id,
              quantity: item.quantity,
              selling_plan: newSellingPlanId
            }]
          })
        });

        await this.fetchCart();
      } catch (err) {
        console.error('ALOHA Cart:', err);
      }
    }

    async fetchProductSellingPlans(handle) {
      // Check cache first
      if (this.productCache[handle]) {
        return this.productCache[handle];
      }

      try {
        const response = await fetch(`/products/${handle}.js`);
        const product = await response.json();

        // Extract Skio selling plans from the product
        const skioGroups = product.selling_plan_groups?.filter(
          group => group.app_id === 'SKIO' &&
                   !group.name.toLowerCase().includes('hidden-')
        ) || [];

        // Flatten all selling plans with frequency info
        const sellingPlans = [];
        skioGroups.forEach(group => {
          group.selling_plans.forEach(plan => {
            // Parse frequency from name (e.g., "Every 1 Month" → 30 days)
            let days = 30; // default
            if (plan.name.includes('2 Month')) days = 60;
            else if (plan.name.includes('3 Month')) days = 90;

            sellingPlans.push({
              id: plan.id,
              name: plan.name,
              days: days,
              discount: plan.price_adjustments?.[0]?.value || 10
            });
          });
        });

        // Cache the result
        this.productCache[handle] = {
          product,
          sellingPlans,
          hasSubscription: sellingPlans.length > 0
        };

        return this.productCache[handle];
      } catch (err) {
        console.warn('ALOHA Cart: Could not fetch product', handle, err);
        return { product: null, sellingPlans: [], hasSubscription: false };
      }
    }

    getSellingPlanId(item, days, productData = null) {
      // Try from cached product data
      if (productData?.sellingPlans?.length) {
        const match = productData.sellingPlans.find(p => p.days == days);
        if (match) return match.id;
        // Return first available if no exact match
        return productData.sellingPlans[0].id;
      }

      // Fallback: use current selling plan allocation
      if (item.selling_plan_allocation) {
        return item.selling_plan_allocation.selling_plan.id;
      }

      return null;
    }

    updateBanner(total) {
      // Hide all banners first
      if (this.bannerDefault) this.bannerDefault.style.display = 'none';
      if (this.bannerStretcher) this.bannerStretcher.style.display = 'none';
      if (this.bannerSitewide) this.bannerSitewide.style.display = 'none';
      if (this.bannerLaunch) this.bannerLaunch.style.display = 'none';

      // VIP members get their own banner (rendered server-side)
      if (this.config.isVip) return;

      const { promoMode } = this.config;

      switch (promoMode) {
        case 'cart_stretcher':
          this.updateStretcherBanner(total);
          break;
        case 'sitewide_sale':
          if (this.bannerSitewide) this.bannerSitewide.style.display = 'block';
          break;
        case 'new_launch':
          if (this.bannerLaunch) this.bannerLaunch.style.display = 'block';
          break;
        case 'default':
        default:
          this.updateDefaultBanner(total);
          break;
      }
    }

    updateDefaultBanner(total) {
      if (!this.bannerDefault) return;

      // Hide banner if setting is enabled
      if (this.config.hideShippingBanner) {
        this.bannerDefault.style.display = 'none';
        return;
      }

      this.bannerDefault.style.display = 'block';

      const { freeShippingThreshold, enableGift, giftThreshold } = this.config;

      // Calculate progress based on highest active threshold
      const maxThreshold = enableGift && giftThreshold > 0 ? giftThreshold : freeShippingThreshold;
      const progress = Math.min(100, (total / maxThreshold) * 100);

      // Fill bar
      if (this.bannerFill) {
        this.bannerFill.style.width = progress + '%';
      }

      // Amount display
      if (this.bannerAmount) {
        this.bannerAmount.textContent = `${formatMoney(total)} / ${formatMoney(maxThreshold)}`;
      }

      // Pin states
      const shipReached = total >= freeShippingThreshold;
      if (this.pinShip) this.pinShip.classList.toggle('reached', shipReached);

      // Handle gift pin visibility and state
      const pinGiftLabel = document.getElementById('ah-pin-gift-label');
      if (enableGift && giftThreshold > 0) {
        const giftReached = total >= giftThreshold;
        if (this.pinGift) {
          this.pinGift.style.display = 'block';
          this.pinGift.classList.toggle('reached', giftReached);
        }
        if (pinGiftLabel) pinGiftLabel.style.display = 'block';
      } else {
        if (this.pinGift) this.pinGift.style.display = 'none';
        if (pinGiftLabel) pinGiftLabel.style.display = 'none';
      }

      // Copy
      if (this.bannerCopy) {
        if (enableGift && giftThreshold > 0) {
          const giftReached = total >= giftThreshold;
          if (giftReached) {
            this.bannerCopy.innerHTML = '<b>Free shipping unlocked</b> · <b>Free gift unlocked!</b>';
          } else if (shipReached) {
            const toGift = giftThreshold - total;
            this.bannerCopy.innerHTML = `<b>Free shipping unlocked</b> · ${formatMoney(toGift)} to free gift`;
          } else {
            const toShip = freeShippingThreshold - total;
            this.bannerCopy.innerHTML = `Add <b>${formatMoney(toShip)}</b> more for <b>free shipping</b>`;
          }
        } else {
          // Gift disabled - only show shipping progress
          if (shipReached) {
            this.bannerCopy.innerHTML = '<b>Free shipping unlocked!</b> 🎉';
          } else {
            const toShip = freeShippingThreshold - total;
            this.bannerCopy.innerHTML = `Add <b>${formatMoney(toShip)}</b> more for <b>free shipping</b>`;
          }
        }
      }
    }

    updateStretcherBanner(total) {
      if (!this.bannerStretcher) return;
      this.bannerStretcher.style.display = 'block';

      const { stretcherT1, stretcherT1Discount, stretcherT2, stretcherT2Discount } = this.config;
      const progress = Math.min(100, (total / stretcherT2) * 100);

      // Fill bar
      if (this.stretcherFill) {
        this.stretcherFill.style.width = progress + '%';
      }

      // Amount display
      if (this.stretcherAmount) {
        this.stretcherAmount.textContent = `${formatMoney(total)} / ${formatMoney(stretcherT2)}`;
      }

      // Copy
      if (this.stretcherCopy) {
        const t1Reached = total >= stretcherT1;
        const t2Reached = total >= stretcherT2;

        if (t2Reached) {
          this.stretcherCopy.innerHTML = `<b>${stretcherT2Discount} off</b> unlocked! 🎉`;
        } else if (t1Reached) {
          const toT2 = stretcherT2 - total;
          this.stretcherCopy.innerHTML = `<b>${stretcherT1Discount} off</b> unlocked · ${formatMoney(toT2)} to save <b>${stretcherT2Discount}</b>`;
        } else {
          const toT1 = stretcherT1 - total;
          this.stretcherCopy.innerHTML = `Spend <b>${formatMoney(toT1)}</b> more to save <b>${stretcherT1Discount}</b>`;
        }
      }
    }

    updateTier(items) {
      if (!this.tierSection) return;

      const subItems = items.filter(i => i.selling_plan_allocation);
      const subQty = subItems.reduce((s, i) => s + i.quantity, 0);

      if (subQty === 0) {
        this.tierSection.style.display = 'none';
        return;
      }

      this.tierSection.style.display = 'block';

      // Step states
      this.tierSteps.forEach((step, idx) => {
        if (!step) return;
        const tier = idx + 1;
        const done = subQty >= tier;
        const active = subQty === tier || (tier === 3 && subQty >= 3);
        step.classList.toggle('done', done && !active);
        step.classList.toggle('active', active);
      });

      // Fill line (0%, 50%, 100%) - adjusted for tier pills
      if (this.tierFill) {
        let fillPct = 0;
        if (subQty >= 3) fillPct = 100;
        else if (subQty === 2) fillPct = 50;
        else if (subQty === 1) fillPct = 0;
        this.tierFill.style.width = `calc(${fillPct}% - 24px)`;
      }

      // Savings - autoship tier discount only, identified by its allocation
      // (Skio tier discounts are titled "10% off 1" / "15% off 2" / "25% off 3";
      // original_price - final_price would also count promo-code allocations)
      const savings = subItems.reduce((s, i) => s + this.autoshipAllocated(i), 0);

      if (this.tierSavings) {
        if (savings === 0 && this.hasPromoApplied(subItems)) {
          // Shopify drops the tier discount when a non-combinable code is applied
          this.tierSavings.textContent = 'Promo code applied';
        } else if (savings === 0 && this.hasCompetingDiscount()) {
          // Same displacement via an automatic discount (e.g. "BYOBundle | 15% Off",
          // combinesWith.productDiscounts = false) — no code anywhere in cart.js,
          // so without this branch the bar would claim "You're saving $0.00"
          this.tierSavings.textContent = 'Discount applied';
        } else {
          this.tierSavings.textContent = `You're saving ${formatMoney(savings)}`;
        }
      }

      // Compact message - only show at max savings
      if (this.tierMsg) {
        if (subQty >= 3) {
          this.tierMsg.innerHTML = `Max savings unlocked`;
        } else {
          this.tierMsg.innerHTML = '';
        }
      }
    }

    // Amount allocated to a line by the Skio autoship tier discount (in cents).
    // Allocation amounts are line totals, not per-unit.
    autoshipAllocated(item) {
      if (!item.selling_plan_allocation) return 0;
      return (item.line_level_discount_allocations || []).reduce((s, alloc) => {
        const app = alloc.discount_application || {};
        const isTier = app.type !== 'discount_code' && /^\d+% off \d+/.test(app.title || '');
        return s + (isTier ? alloc.amount : 0);
      }, 0);
    }

    hasPromoApplied(items) {
      if ((this.cartData?.discount_codes || []).some(d => d.applicable)) return true;
      return items.some(i => (i.line_level_discount_allocations || [])
        .some(a => a.discount_application?.type === 'discount_code'));
    }

    // Any non-tier discount in the cart (automatic promos, order-scope apps).
    // When one of these wins over the Skio tier, Shopify strips the tier
    // allocation cart-wide, so zero tier savings + a competing discount means
    // "displaced", not "no savings".
    hasCompetingDiscount() {
      if ((this.cartData?.cart_level_discount_applications || []).length > 0) return true;
      return (this.cartData?.items || []).some(i => (i.line_level_discount_allocations || [])
        .some(a => !/^\d+% off \d+/.test(a.discount_application?.title || '')));
    }

    updateFooter(items, total) {
      // Total savings: line-level allocations (autoship tier + product-scope
      // promo codes) plus order-scope code discounts at cart level
      const lineSavings = items.reduce((s, i) => s + (i.original_line_price - i.final_line_price), 0);
      const cartLevelDiscount = (this.cartData?.cart_level_discount_applications || [])
        .reduce((s, a) => s + (a.total_allocated_amount || 0), 0);
      const totalSavings = lineSavings + cartLevelDiscount;

      if (this.savingsRow && this.savingsAmount) {
        if (totalSavings > 0) {
          this.savingsRow.style.display = 'flex';
          this.savingsAmount.textContent = `−${formatMoney(totalSavings)}`;
        } else {
          this.savingsRow.style.display = 'none';
        }
      }

      // Shipping status
      const { freeShippingThreshold, isVip, vipTier } = this.config;
      if (this.shipText && this.shipCheck) {
        if (isVip) {
          this.shipCheck.style.display = 'inline';
          this.shipText.innerHTML = `Free shipping with ${vipTier} membership`;
        } else if (total >= freeShippingThreshold) {
          this.shipCheck.style.display = 'inline';
          this.shipText.innerHTML = '<b>Free shipping</b> unlocked';
        } else {
          this.shipCheck.style.display = 'none';
          const remaining = freeShippingThreshold - total;
          this.shipText.innerHTML = `Add <b>${formatMoney(remaining)}</b> for free shipping`;
        }
      }

      // Total
      if (this.totalEl) {
        this.totalEl.textContent = formatMoney(total);
      }

      // Checkout button amount
      if (this.checkoutAmt) {
        this.checkoutAmt.textContent = formatMoney(total);
      }
    }

    updateHeaderCount() {
      if (!this.cartData) return;
      const items = this.cartData.items.filter(i => i.vendor !== 'SavedBy');
      const count = items.reduce((s, i) => s + i.quantity, 0);

      document.querySelectorAll('.js-go-cart-counter, [data-cart-count]').forEach(el => {
        el.textContent = count;
        el.classList.toggle('visuallyhidden', count === 0);
      });
    }

    scrollUpsell(dir) {
      if (!this.upsellRail) return;
      const scrollAmt = 160;
      this.upsellRail.scrollBy({ left: scrollAmt * dir, behavior: 'smooth' });
    }

    async fetchUpsell() {
      if (!this.config.enableUpsell || !this.config.upsellCollection || !this.upsellRail) {
        if (this.upsellSection) this.upsellSection.style.display = 'none';
        return;
      }

      // Don't refetch if we already have products
      if (this.upsellProducts.length > 0) {
        this.renderUpsell();
        return;
      }

      try {
        const handle = this.config.upsellCollection;
        const res = await fetch(`/collections/${handle}/products.json?limit=8`);
        if (!res.ok) throw new Error(`Failed to fetch: ${res.status}`);
        const data = await res.json();
        this.upsellProducts = data.products || [];
        this.renderUpsell();
      } catch (err) {
        if (this.upsellSection) this.upsellSection.style.display = 'none';
      }
    }

    renderUpsell() {
      if (!this.upsellRail || this.upsellProducts.length === 0) {
        if (this.upsellSection) this.upsellSection.style.display = 'none';
        return;
      }

      // Get current cart product IDs to exclude
      const cartIds = new Set((this.cartData?.items || []).map(i => i.product_id));
      const products = this.upsellProducts.filter(p => !cartIds.has(p.id)).slice(0, 6);

      if (products.length === 0) {
        if (this.upsellSection) this.upsellSection.style.display = 'none';
        return;
      }

      const template = document.getElementById('ah-tile-template');
      if (!template) return;

      this.upsellRail.innerHTML = '';

      products.forEach(product => {
        const variant = product.variants[0];
        if (!variant) return;

        const tile = template.content.cloneNode(true).querySelector('.ah-tile');
        if (!tile) return;

        tile.dataset.productId = product.id;
        tile.dataset.variantId = variant.id;

        const img = tile.querySelector('.ah-tile__media img');
        if (img && product.images && product.images[0]) {
          img.src = this.resizeImg(product.images[0].src, '200x200');
          img.alt = product.title;
        }

        // Set product links (image and title)
        const productUrl = `/products/${product.handle}`;
        const mediaLink = tile.querySelector('.ah-tile__media-link');
        const nameLink = tile.querySelector('.ah-tile__name-link');
        if (mediaLink) mediaLink.href = productUrl;
        if (nameLink) nameLink.href = productUrl;

        this.setTileTitle(tile, product);

        // Price from products.json is a string like "34.99" - convert to cents
        const priceInCents = Math.round(parseFloat(variant.price) * 100);
        const priceEl = tile.querySelector('.ah-tile__price');
        if (priceEl) priceEl.textContent = formatMoney(priceInCents);

        const addBtn = tile.querySelector('.ah-tile__add');
        if (addBtn) {
          addBtn.addEventListener('click', (e) => {
            e.preventDefault();
            this.addToCart(variant.id, 1);
          });
        }

        this.upsellRail.appendChild(tile);
      });

      if (this.upsellSection) this.upsellSection.style.display = 'block';
    }

    // ALOHA titles are "Flavor - Type"; tiles show the parts separately so
    // the flavor never ellipsizes (same split as blog rail / collections cards)
    setTileTitle(tile, product) {
      const parts = product.title.split(' - ');
      const name = parts[0];
      const type = parts.length > 1 ? parts[parts.length - 1] : (product.product_type || '');
      const nameEl = tile.querySelector('.ah-tile__name');
      const typeEl = tile.querySelector('.ah-tile__type');
      if (nameEl) nameEl.textContent = name;
      if (typeEl) {
        typeEl.textContent = type;
        typeEl.style.display = type ? '' : 'none';
      }
      const nameLink = tile.querySelector('.ah-tile__name-link');
      if (nameLink) nameLink.title = product.title;
    }

    async addToCart(variantId, quantity = 1) {
      try {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity })
        });
        await this.fetchCart();
      } catch (err) {
        console.error('Add to cart error:', err);
      }
    }

    resizeImg(src, size) {
      if (!src || !src.includes('.shopify.com')) return src;
      return src.replace(/(_\d+x\d+)?(\.(jpg|jpeg|png|gif|webp))/i, `_${size}$2`);
    }
  }

  // Init
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => { window.AlohaCart = new AlohaCart(); });
  } else {
    window.AlohaCart = new AlohaCart();
  }
})();
